import {KeyVault, Office, Role, UserRoleForm, VaultManagerRole} from "../../api/Model";
import {inputChange, mapSetState, selectChangeEvent, SetState} from "../../util/FormHandlers";
import React, {useMemo} from "react";
import {Box, FormControl, InputLabel, MenuItem, Select, TextField} from "@mui/material";
import {RemoteValue, useRemoteValue} from "../../util/RemoteValue";
import {useOfficeStore} from "../../api/OfficeStore";
import {useGlobalKeyVaultStore} from "../../api/GlobalKeyVaultStore";
import {stringCmp} from "../../util/SortUtil";
import {officeIdOfRole} from "../../util/access/AccessRules";


function patchRole(
    tree: { office: Office, keyVaults: KeyVault[] }[],
    nextRole: {
        type?: Role["type"],
        officeId?: number,
        vaultId?: number,
    },
): (prevRole: Role) => Role {
    return prevRole => {
        const patchedOfficeId: Office['id'] = nextRole.officeId ||
            officeIdOfRole(prevRole) ||
            tree[0]?.office?.id ||
            0;

        const patchedVaultId: KeyVault['id'] = nextRole.vaultId || (
            prevRole.type === 'vault-manager' && prevRole.officeId === patchedOfficeId
                ? prevRole.vaultId
                : tree.find(({office}) => office.id === patchedOfficeId)?.keyVaults?.[0]?.id || 0
        )

        const patchedRoleType = nextRole.type || prevRole.type;

        switch (patchedRoleType) {
            case "guest":
                return {type: 'guest'};
            case "system-admin":
                return {type: 'system-admin'};
            case "office-admin":
                return {type: 'office-admin', officeId: patchedOfficeId};
            case "vault-manager":
                return {type: 'vault-manager', officeId: patchedOfficeId, vaultId: patchedVaultId};
        }
    };
}

export function UserRoleDialog({form, setForm}: {
    form: UserRoleForm;
    setForm: SetState<UserRoleForm>;
}) {
    const keyVaults = useGlobalKeyVaultStore(state => state.remoteValue);
    const offices = useOfficeStore(state => state.remoteValue);

    const keyVaultTreeRV = useMemo(
        () => RemoteValue
            .combine({keyVaults, offices})
            .map(({keyVaults, offices}) =>
                [...offices]
                    .sort(stringCmp('name'))
                    .map(office => ({
                        office,
                        keyVaults: keyVaults
                            .filter(vault => vault.officeId === office.keyOfficeId)
                            .sort(stringCmp('name')),
                    }))
            ),
        [keyVaults, offices],
    );
    const tree = useRemoteValue(keyVaultTreeRV, []);

    const handlers = useMemo(() => {
        const setRole = mapSetState(setForm, 'role');

        return ({
            handleEmailChange: inputChange(setForm, newValue => ({email: newValue})),
            handleRoleTypeChange: selectChangeEvent<Role["type"]>(newRoleType => {
                switch (newRoleType) {
                    case 'guest':
                    case 'vault-manager':
                    case 'office-admin':
                    case 'system-admin':
                        setRole(patchRole(tree, {type: newRoleType}));
                        break;
                }
            }),
            handleOfficeChange: selectChangeEvent<number | ''>(newOfficeId => {
                setRole(patchRole(tree, {officeId: Number(newOfficeId)}));
            }),
            handleVaultChange: selectChangeEvent<number | ''>(newVaultId => {
                setRole(patchRole(tree, {vaultId: Number(newVaultId)}));
            }),
        });
    }, [setForm, tree]);

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <TextField
                autoFocus
                required
                margin="dense"
                id="email"
                label="Email"
                type="email"
                fullWidth
                variant="outlined"
                value={form.email}
                onChange={handlers.handleEmailChange}
            />

            <FormControl fullWidth>
                <InputLabel id="role-type-label">Role</InputLabel>
                <Select
                    labelId="role-type-label"
                    id="role-type"
                    value={form.role.type}
                    label="Role"
                    onChange={handlers.handleRoleTypeChange}
                >
                    <MenuItem value="guest">Guest</MenuItem>
                    <MenuItem value="vault-manager">Vault Manager</MenuItem>
                    <MenuItem value="office-admin">Office Admin</MenuItem>
                    <MenuItem value="system-admin">System Admin</MenuItem>
                </Select>
            </FormControl>

            {(form.role.type === 'office-admin' || form.role.type === 'vault-manager') && (
                <FormControl fullWidth>
                    <InputLabel id="office-label">Office</InputLabel>
                    <Select
                        labelId="office-label"
                        id="office"
                        required
                        value={form.role.officeId || ''}
                        label="Office"
                        onChange={handlers.handleOfficeChange}
                    >
                        {tree.map(({office}) => (
                            <MenuItem key={office.id} value={office.id}>{office.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {form.role.type === 'vault-manager' && (
                <FormControl fullWidth>
                    <InputLabel id="vault-label">Vault</InputLabel>
                    <Select
                        labelId="vault-label"
                        id="vault"
                        required
                        value={form.role.vaultId || ''}
                        label="Vault"
                        onChange={handlers.handleVaultChange}
                    >
                        {(tree
                            .find(({office}) => (form.role as VaultManagerRole).officeId === office.id)
                            ?.keyVaults || [])
                            .map((vault) => (
                                <MenuItem key={vault.id} value={vault.id}>{vault.name}</MenuItem>
                            ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
}
